import { Link } from 'gatsby';
import { GatsbyImage, getImage } from 'gatsby-plugin-image';
import React from 'react';
import Skeleton from 'react-loading-skeleton';

import { Breakpoints } from '../../constants';
import useWindowSize from '../../hooks/useWindowSize';
import { BasicProps, Post } from '../../types';
import * as styles from './ArticleCard.module.scss';

export interface Props extends BasicProps {
  aspectRatio?: string;
  largeCard?: boolean;
  blogPostData: {
    post: Post;
  };
  isFeatured?: boolean;
  isSkeleton?: boolean;
  imageContainerClassName?: string;
}

const ArticleCard: React.FC<Props> = ({
  blogPostData,
  className,
  largeCard,
  isFeatured,
  aspectRatio = '4 / 3',
  'data-testid': testId,
  isSkeleton,
  imageContainerClassName,
}) => {
  const data = blogPostData.post;
  const { isWiderThan } = useWindowSize();
  const image = getImage(data?.featuredImage?.node?.gatsbyImage || {});
  let tag = isFeatured && 'Featured';

  if (!isFeatured && data?.tags?.nodes?.length) {
    tag =
      (data.tags as Queries.WpTagConnection).nodes.filter((tagToFilter) =>
        ['editor-pick'].includes(tagToFilter?.slug || ''),
      )[0]?.name || '';
  }

  const content = (
    <div
      data-testid={testId && testId + 'Content'}
      className={`${styles.articleCardContent} ${
        largeCard ? styles.largeCard : ''
      }`}
    >
      {isSkeleton ? (
        <Skeleton />
      ) : (
        <>
          {data?.categories?.nodes?.map((category, index, array) => (
            <Link
              className={styles.gatsbyLink}
              key={category?.name}
              to={category?.link || ''}
            >
              <span
                className={`${styles.categoryLink} ${
                  largeCard ? '' : styles.smallCategoryLink
                } body2 emphasis`}
              >
                {category?.name}
              </span>
              {array.length > 0 && index < array.length - 1 ? (
                <span className={!largeCard ? styles.categoriesComma : ''}>
                  ,{' '}
                </span>
              ) : (
                ''
              )}
            </Link>
          ))}
          <span className={`${styles.categoriesHyphen} body2 emphasis`}>
            {' '}
            -{' '}
          </span>
        </>
      )}

      <span
        className={`${styles.charcoalText} ${
          largeCard ? styles.largeCard : ''
        } body2`}
      >
        {isSkeleton ? (
          <Skeleton />
        ) : (
          new Date(data?.date || '').toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
          })
        )}
      </span>
      <Link className={styles.gatsbyLink} to={data?.link || ''}>
        {largeCard ? (
          <h4>{isSkeleton ? <Skeleton /> : data?.title}</h4>
        ) : (
          <h5>{isSkeleton ? <Skeleton /> : data?.title}</h5>
        )}
      </Link>
      {/* // eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
      {/* @ts-expect-error this props are optional on the sub-type featuredBlogPost */}
      {largeCard && data?.template?.blog?.featuredArticleDescription ? (
        <p className={`${styles.largeCardDescription} body1`}>
          {isSkeleton ? (
            <Skeleton />
          ) : (
            // @ts-expect-error this props are optional on the sub-type featuredBlogPost */
            data?.template?.blog?.featuredArticleDescription
          )}
        </p>
      ) : (
        ''
      )}
    </div>
  );

  return (
    <div
      className={`${className || ''} ${styles.articleCard} ${
        largeCard ? styles.largeCard : ''
      }`}
      data-testid={testId && testId + 'Container'}
    >
      <div
        data-testid={testId && testId + 'ImageFrame'}
        className={`${styles.articleCardImageContainer} ${imageContainerClassName}`}
        style={isWiderThan(Breakpoints.Lg) ? { aspectRatio } : {}}
      >
        {tag && (
          <div className={`${styles.imageTag} darkContainer`}>
            <span className="body2 emphasis">{tag}</span>
          </div>
        )}
        {isSkeleton ? (
          <Skeleton height="100" width="100" />
        ) : (
          <Link to={data?.link || ''}>
            {image && (
              <GatsbyImage
                data-testid={testId && testId + 'ArticleImage'}
                className={styles.imageWrapper}
                style={{ position: 'absolute' }}
                image={image}
                alt={data?.featuredImage?.node?.altText || ''}
              />
            )}
          </Link>
        )}
      </div>
      {content}
    </div>
  );
};

export default ArticleCard;
